/**
 * miroSnapshot — lift a dialect-shaped Miro board read into a
 * SemanticSnapshot through the Miro profile defaults + the workspace's
 * semantic map (connector_configs.field_mappings v3) + visual signifier
 * rules. Pure and deterministic: all uncertainty is recorded on assertions,
 * none is resolved here.
 */

import type { MiroExcursionBoardRead } from './miroExcursionApi';
import {
  resolveRelationshipCue,
  type MiroColorRule,
  type RelationshipCueRule,
  type TypeMapRule,
} from '../fieldMappingRegistry';
import { resolveSignifierPatch } from '../whiteboardSignifiers';
import { DEFAULT_COLOR_TO_CATEGORY } from '../whiteboardImportService';
import type { ItemLanguageType } from '../../types/prioritization/lifecycle';
import type { RelationshipDirectionality } from '../../types/prioritization/itemRelationships';
import {
  MIRO_DEFAULT_RELATIONSHIP_MAP,
  MIRO_DEFAULT_TYPE_MAP,
} from './profiles/miro';
import type { SemanticObject, SemanticRelation, SemanticSnapshot } from './snapshot';
import { emptySnapshot } from './snapshot';

export interface MiroSnapshotMaps {
  /** Workspace v3 relationship rules; profile defaults fill the gaps. */
  relationshipMap?: RelationshipCueRule[];
  /** Workspace v3 native-type routing; profile defaults fill the gaps. */
  typeMap?: TypeMapRule[];
  /** Workspace visual signifier rules for the 'items' entity. */
  colorRules?: MiroColorRule[];
}

/** Named Miro palette → the color vocabulary the signifier rules use. */
function normalizeColor(fillColor: string | undefined): string | undefined {
  if (!fillColor) return undefined;
  const c = fillColor.toLowerCase().trim();
  // Miro palette names like 'light_yellow' / 'dark_blue' → base color tokens.
  if (c.startsWith('light_')) return c.slice(6);
  if (c.startsWith('dark_')) return c.slice(5);
  if (c === 'violet') return 'purple';
  return c;
}

function routeNativeType(nativeType: string, typeMap: TypeMapRule[]): TypeMapRule['target'] {
  const rule = typeMap.find(r => r.nativeType === nativeType);
  if (rule) return rule.target;
  const fallback = MIRO_DEFAULT_TYPE_MAP.find(r => r.nativeType === nativeType);
  return fallback?.target ?? 'ignore';
}

/**
 * Build the SemanticSnapshot for a board read. `scopeFrameId` is the
 * excursion's own frame — membership in it is scope, not a `contains`
 * relationship; membership in any OTHER frame is a spatial containment cue.
 */
export function buildMiroSnapshot(
  read: MiroExcursionBoardRead,
  maps: MiroSnapshotMaps,
  options: { connectorType?: string; scopeFrameId?: string; capturedAt?: string } = {},
): SemanticSnapshot {
  const capturedAt = options.capturedAt ?? new Date().toISOString();
  const snapshot = emptySnapshot({
    connectorType: options.connectorType ?? 'miro',
    containerId: read.board.id,
    containerUrl: read.board.viewLink,
    capturedAt,
  });

  const typeMap = [...(maps.typeMap ?? []), ...MIRO_DEFAULT_TYPE_MAP];
  const relationshipRules = [...(maps.relationshipMap ?? []), ...MIRO_DEFAULT_RELATIONSHIP_MAP];
  const colorRules = maps.colorRules ?? [];

  // Regions first (frames) so objects can reference them.
  for (const frame of read.frames) {
    snapshot.regions.push({
      externalId: frame.id,
      title: frame.title,
      kind: 'frame',
      layout: { x: frame.x, y: frame.y, width: frame.width, height: frame.height },
    });
  }

  // Normalize positions into [0,1] against the observed bounding box so
  // proximity matching is resolution-independent (whiteboard convention).
  const xs = read.items.map(i => i.x);
  const ys = read.items.map(i => i.y);
  const minX = Math.min(...xs, 0);
  const maxX = Math.max(...xs, 1);
  const minY = Math.min(...ys, 0);
  const maxY = Math.max(...ys, 1);
  const spanX = Math.max(maxX - minX, 1);
  const spanY = Math.max(maxY - minY, 1);

  for (const item of read.items) {
    const target = routeNativeType(item.type, typeMap);
    if (target === 'ignore' || target === 'region') continue;

    if (target === 'annotation') {
      snapshot.annotations.push({ externalId: item.id, text: item.text });
      continue;
    }

    const color = normalizeColor(item.fillColor);
    const object: SemanticObject = {
      externalId: item.id,
      nativeType: item.type,
      content: { text: item.text, ...(item.description ? { description: item.description } : {}) },
      visual: {
        ...(color ? { fill_color: color } : {}),
        ...(item.shape ? { shape: item.shape } : {}),
      },
      layout: {
        x: (item.x - minX) / spanX,
        y: (item.y - minY) / spanY,
        ...(item.parentFrameId ? { regionId: item.parentFrameId } : {}),
      },
      asserted: {},
    };

    // Category: workspace signifier rules override the shipped defaults —
    // exactly the whiteboard precedence (rules beat DEFAULT_COLOR_TO_CATEGORY).
    if (color) {
      const patch = resolveSignifierPatch(
        { fillColor: color, ...(item.shape ? { shape: item.shape } : {}) },
        colorRules,
      );
      const ruleCategory = patch.itemCategory as ItemLanguageType | undefined;
      const defaultCategory = DEFAULT_COLOR_TO_CATEGORY[color] as ItemLanguageType | undefined;
      if (ruleCategory) {
        object.asserted.itemCategory = { value: ruleCategory, confidence: 0.9, basis: 'visual_language' };
      } else if (defaultCategory) {
        object.asserted.itemCategory = { value: defaultCategory, confidence: 0.8, basis: 'visual_language' };
      }
      if (patch.altitudeLevel !== undefined) {
        object.asserted.altitudeLevel = { value: Number(patch.altitudeLevel), confidence: 0.9, basis: 'visual_language' };
      }
      if (patch.metadata && Object.keys(patch.metadata).length > 0) {
        object.asserted.attributes = Object.fromEntries(
          Object.entries(patch.metadata).map(([k, v]) => [
            k,
            { value: v, confidence: 0.9, basis: 'visual_language' as const },
          ]),
        );
      }
    }

    snapshot.objects.push(object);
  }

  const objectIds = new Set(snapshot.objects.map(o => o.externalId));

  // Explicit connectors → relations. Caption beats line style (precedence
  // enforced by resolveRelationshipCue).
  for (const connector of read.connectors) {
    if (!connector.startItemId || !connector.endItemId) {
      snapshot.warnings.push(`Connector ${connector.id} has a dangling endpoint — skipped.`);
      continue;
    }
    if (!objectIds.has(connector.startItemId) || !objectIds.has(connector.endItemId)) {
      // Endpoint routed to annotation/region/ignore — not an item relation.
      snapshot.unrecognized.push({ kind: 'connector', id: connector.id });
      continue;
    }
    const cues: Array<{ kind: 'caption' | 'edge_kind'; value: string }> = [];
    if (connector.caption) cues.push({ kind: 'caption', value: connector.caption });
    cues.push({ kind: 'edge_kind', value: connector.strokeStyle || 'normal' });

    const rule = resolveRelationshipCue(cues, relationshipRules);
    if (!rule) {
      snapshot.unrecognized.push({ kind: 'connector', id: connector.id, caption: connector.caption });
      continue;
    }
    snapshot.relations.push(buildRelation(connector.startItemId, connector.endItemId, rule, connector.caption || connector.strokeStyle));
  }

  // Spatial containment: membership in a non-scope frame is a `contains` cue.
  const containmentRule = resolveRelationshipCue([{ kind: 'containment', value: 'frame' }], relationshipRules);
  if (containmentRule) {
    for (const object of snapshot.objects) {
      const regionId = object.layout?.regionId;
      if (!regionId || regionId === options.scopeFrameId) continue;
      const region = snapshot.regions.find(r => r.externalId === regionId);
      if (!region) continue;
      snapshot.relations.push({
        sourceExternalId: regionId,
        targetExternalId: object.externalId,
        canonicalType: containmentRule.canonicalType as SemanticRelation['canonicalType'],
        ...(containmentRule.subtype ? { subtype: containmentRule.subtype } : {}),
        directionality: (containmentRule.directionality ?? 'directed') as RelationshipDirectionality,
        cue: `inside frame “${region.title}”`,
        assertion: {
          value: containmentRule.canonicalType as SemanticRelation['canonicalType'],
          confidence: containmentRule.defaultConfidence,
          basis: containmentRule.basis,
        },
      });
    }
  }

  return snapshot;
}

function buildRelation(
  sourceExternalId: string,
  targetExternalId: string,
  rule: RelationshipCueRule,
  cue: string,
): SemanticRelation {
  return {
    sourceExternalId,
    targetExternalId,
    canonicalType: rule.canonicalType as SemanticRelation['canonicalType'],
    ...(rule.subtype ? { subtype: rule.subtype } : {}),
    directionality: (rule.directionality ?? 'directed') as RelationshipDirectionality,
    cue,
    assertion: {
      value: rule.canonicalType as SemanticRelation['canonicalType'],
      confidence: rule.defaultConfidence,
      basis: rule.basis,
    },
  };
}
